import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEmpFeatureFlag } from './useEmpFeatureFlag';
import { useToast } from './use-toast';
import { GEO_INTEL_QUERY_KEY } from './useGeoIntelData';

export type TipoContratoOperacional = 'preco_unitario' | 'preco_global' | 'ordem_servico';

export const TIPO_CONTRATO_OPERACIONAL_OPTIONS: Array<{ value: TipoContratoOperacional; label: string }> = [
  { value: 'preco_unitario', label: 'Preço Unitário (PPU)' },
  { value: 'preco_global', label: 'Preço Global (EAP)' },
  { value: 'ordem_servico', label: 'Ordem de Serviço (UBS)' },
];

export const getTipoContratoOperacionalLabel = (tipo: TipoContratoOperacional) =>
  TIPO_CONTRATO_OPERACIONAL_OPTIONS.find(t => t.value === tipo)?.label || tipo;

export type PesoModelo = 'absoluto' | 'hierarquico';

export const PESO_MODELO_OPTIONS: Array<{ value: PesoModelo; label: string; description: string }> = [
  { value: 'absoluto', label: 'Absoluto (Flat)', description: 'Peso global direto por atividade. Modelo padrão.' },
  { value: 'hierarquico', label: 'Hierárquico (EAP)', description: 'Peso relativo ao nível pai. Sistema deriva peso global automaticamente.' },
];

export interface EmpContrato {
  id: string;
  cluster_id: string;
  contract_id: string | null;
  numero_contrato: string;
  nome: string;
  descricao: string | null;
  objeto: string | null;
  contratante: string;
  contratada: string;
  cnpj_contratada: string | null;
  gestor_contrato_id: string | null;
  fiscal_medicao_id: string | null;
  valor_global: number;
  valor_aditivos_acumulado: number;
  valor_original: number | null;
  data_assinatura: string | null;
  data_inicio: string;
  data_fim_prevista: string;
  data_fim_real: string | null;
  prazo_meses: number | null;
  status: string;
  versao: number;
  ativo: boolean;
  dados_extras: Record<string, unknown> | null;
  tipo_medicao: 'preco_unitario' | 'epc' | 'hibrido';
  tipo_contrato_operacional: TipoContratoOperacional;
  ubs_quantidade_contratada: number;
  ubs_preco_unitario: number;
  municipio_ibge: string | null;
  percentual_retencao: number;
  data_base_reajuste: string | null;
  prazo_reajuste: number | null;
  peso_modelo: PesoModelo;
  bac_referencia_eap: number | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export type TipoMedicao = 'preco_unitario' | 'epc' | 'hibrido';

export const TIPO_MEDICAO_OPTIONS: Array<{ value: TipoMedicao; label: string; description: string }> = [
  { value: 'preco_unitario', label: 'Por Item', description: 'Pagamento proporcional às quantidades executadas dos itens.' },
  { value: 'epc', label: 'Marcos ou Eventos', description: 'Pagamento realizado quando etapas ou marcos definidos são concluídos.' },
  { value: 'hibrido', label: 'Híbrido', description: 'Combinação de medição por itens e por eventos.' },
];

export const getTipoMedicaoLabel = (tipo: TipoMedicao) =>
  TIPO_MEDICAO_OPTIONS.find(t => t.value === tipo)?.label || tipo;

export type EmpContratoInsert = {
  cluster_id: string;
  numero_contrato: string;
  nome: string;
  descricao?: string | null;
  objeto?: string | null;
  contratante: string;
  contratada: string;
  cnpj_contratada?: string | null;
  valor_global: number;
  valor_original?: number | null;
  data_assinatura?: string | null;
  data_inicio: string;
  data_fim_prevista: string;
  prazo_meses?: number | null;
  tipo_medicao?: TipoMedicao;
  tipo_contrato_operacional?: TipoContratoOperacional;
  ubs_quantidade_contratada?: number;
  ubs_preco_unitario?: number;
  municipio_ibge?: string | null;
  percentual_retencao?: number;
  data_base_reajuste?: string | null;
  prazo_reajuste?: number | null;
  peso_modelo?: PesoModelo;
  bac_referencia_eap?: number | null;
};

export type EmpContratoUpdate = Partial<EmpContratoInsert> & {
  status?: string;
  ativo?: boolean;
};

const STATUS_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  ativo: 'Ativo',
  suspenso: 'Suspenso',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado',
};

export const getStatusLabel = (status: string) => STATUS_LABELS[status] || status;

export const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

export function useEmpContratos() {
  const { user, role } = useAuth();
  const { activeClusterIds } = useEmpFeatureFlag();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const canEdit = role === 'admin' || role === 'coadmin';

  const { data: contratos = [], isLoading } = useQuery({
    queryKey: ['emp-contratos', activeClusterIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_contratos')
        .select('*, emp_planilha_versoes!left(numero_versao)')
        .eq('emp_planilha_versoes.status', 'vigente' as any)
        .in('cluster_id', activeClusterIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Flatten the joined planilha version onto each contract
      return (data || []).map((row: any) => {
        const versoes = row.emp_planilha_versoes;
        const planilha_versao_vigente = Array.isArray(versoes)
          ? versoes[0]?.numero_versao ?? null
          : versoes?.numero_versao ?? null;
        const { emp_planilha_versoes: _, ...rest } = row;
        return { ...rest, planilha_versao_vigente } as EmpContrato & { planilha_versao_vigente: number | null };
      });
    },
    enabled: activeClusterIds.length > 0,
  });

  const createContrato = useMutation({
    mutationFn: async (input: EmpContratoInsert) => {
      if (!user) throw new Error('Usuário não autenticado');
      const { data, error } = await supabase
        .from('emp_contratos')
        .insert({
          ...input,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emp-contratos'] });
      queryClient.invalidateQueries({ queryKey: ['emp-module-stats'] });
      queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });
      // If EPC contract was created, flag was auto-activated — refresh cache
      if (variables.tipo_contrato_operacional === 'preco_global') {
        queryClient.invalidateQueries({ queryKey: ['epc-rerateio-flag'] });
      }
      toast({ title: 'Contrato criado com sucesso' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao criar contrato', description: err.message, variant: 'destructive' });
    },
  });

  const updateContrato = useMutation({
    mutationFn: async ({ id, ...updates }: EmpContratoUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('emp_contratos')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emp-contratos'] });
      queryClient.invalidateQueries({ queryKey: ['emp-module-stats'] });
      queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });
      toast({ title: 'Contrato atualizado com sucesso' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao atualizar contrato', description: err.message, variant: 'destructive' });
    },
  });

  // ── Realtime subscription for automatic updates ──
  useEffect(() => {
    const channel = supabase
      .channel('emp-contratos-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emp_contratos' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['emp-contratos'] });
          queryClient.invalidateQueries({ queryKey: ['emp-contrato'] });
          queryClient.invalidateQueries({ queryKey: ['emp-module-stats'] });
          queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emp_aditivos' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['emp-contratos'] });
          queryClient.invalidateQueries({ queryKey: ['emp-contrato'] });
          queryClient.invalidateQueries({ queryKey: ['emp-aditivos'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emp_workflow_etapas' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['emp-workflow-etapas'] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emp_workflow_aprovacoes' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['emp-workflow-aprovacoes'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    contratos,
    isLoading,
    canEdit,
    createContrato,
    updateContrato,
  };
}
